import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRightLong, faBuilding, faClipboardCheck, faCode, faHelmetSafety,
    faSchool, faSitemap, faTowerBroadcast, faWaveSquare
} from '@fortawesome/free-solid-svg-icons';

import HeaderHome from '../../Components/Header_Home/Header_Home';
import ZenLogoWhite from "../../imgs/ZenLogo_white.svg";
import '../Home/Home.css';
import './AboutUs.css';

const AboutUs = ({ data } : { data : any }) => {

    const [cookies] = useCookies();
    const en = cookies?.language === "en";
    const navigate = useNavigate();

    const loggedIn = data?.userData != null;

    const doing = [
        {
            icon: faTowerBroadcast,
            ro: "Simulatorul de stație", en: "The station simulator",
            roDesc: "Schema monofilară a stației, cu separatoare, întrerupătoare și CLP-uri, pe care se execută manevre — dictate de dispecer, libere sau punctate.",
            enDesc: "The station one-line diagram, with disconnectors, breakers and earthing switches, on which maneuvers are performed — dictated by the dispatcher, free, or scored.",
        },
        {
            icon: faWaveSquare,
            ro: "Partea de joasă tensiune", en: "The low-tension side",
            roDesc: "Editor de scheme electrice cu print A4 / A3, scheme predefinite arătate pas cu pas și depanare cu aparat de măsură virtual.",
            enDesc: "An electrical scheme editor with A4 / A3 printing, predefined schemes shown step by step, and fault-finding with a virtual measuring device.",
        },
        {
            icon: faClipboardCheck,
            ro: "Evaluarea", en: "Assessment",
            roDesc: "Profesorul creează sesiuni, urmărește execuția și vede clasamentul. Punctajul vine din manevrele efectuate, nu din impresie.",
            enDesc: "The teacher creates sessions, follows the execution and sees the leaderboard. The score comes from the maneuvers performed, not from impression.",
        },
    ];

    const audience = [
        {
            icon: faSchool,
            ro: "Licee și școli profesionale", en: "Technical and vocational schools",
            roDesc: "Elevii lucrează pe scheme reale înainte de a ajunge lângă un panou sub tensiune.",
            enDesc: "Students work on real schemes before they ever stand next to a live panel.",
        },
        {
            icon: faHelmetSafety,
            ro: "Electricieni de stație", en: "Station electricians",
            roDesc: "Manevrele se repetă până intră în reflex, inclusiv cele din regim de avarie.",
            enDesc: "Maneuvers are repeated until they become reflex, emergency ones included.",
        },
        {
            icon: faBuilding,
            ro: "Companii", en: "Companies",
            roDesc: "Verificarea și selecția personalului, pe aceleași manevre și cu același punctaj pentru toți.",
            enDesc: "Checking and selecting staff, on the same maneuvers and the same scoring for everyone.",
        },
    ];

    const principles = [
        {
            ro: "Fidelitate față de exploatare", en: "True to the field",
            roDesc: "Comenzile sunt formulate ca în stație, iar ordinea manevrelor contează. Un simulator care iartă ordinea nu antrenează nimic.",
            enDesc: "Commands are worded as they are in the substation, and the order of operations matters. A simulator that forgives the order trains nothing.",
        },
        {
            ro: "Repetiție, nu demonstrație", en: "Repetition, not demonstration",
            roDesc: "O manevră văzută o dată se uită. Modulele sunt construite ca să fie reluate, cu revenire asupra a ceea ce s-a greșit.",
            enDesc: "A maneuver seen once is forgotten. The modules are built to be repeated, coming back to whatever went wrong.",
        },
        {
            ro: "Fără instalare", en: "Nothing to install",
            roDesc: "Totul rulează în browser, pe laptopul din laborator sau pe o tabletă. Contul îl face profesorul, elevul doar se autentifică.",
            enDesc: "Everything runs in the browser, on the lab laptop or on a tablet. The teacher creates the account, the student only signs in.",
        },
    ];

    return (
        <div className="home-container home-page about-page">
            <HeaderHome data={data} activePage="about-us" />

            <div className="content">

                {/* ---- intro ---- */}
                <section className="hp-hero ab-hero">
                    <div className="hp-hero-text">
                        <p className="hp-kicker">
                            <span className="hp-kicker-dot" />
                            {en ? "About us" : "Despre noi"}
                        </p>

                        <h1 className="hp-title">
                            {en ? <>A simulator built <span>for the substation</span>.</>
                                : <>Un simulator construit <span>pentru stație</span>.</>}
                        </h1>

                        <p className="hp-lead">
                            {en
                                ? "ZenVolt came out of a simple observation: an electrician learns maneuvers on live equipment, where a mistake is expensive and there is no second attempt. We moved that practice into a simulator — the same one-line diagram, the same dispatcher, the same order of operations, without the consequences."
                                : "ZenVolt a pornit de la o observație simplă: electricianul învață manevrele pe instalație, acolo unde greșeala e scumpă și nu există a doua încercare. Am mutat exercițiul în simulator — aceeași schemă monofilară, același dispecer, aceeași ordine a manevrelor, fără consecințe."}
                        </p>

                        <div className="hp-cta-row">
                            <button type="button" className="hp-cta" onClick={() => navigate(loggedIn ? "/categories" : "/login")}>
                                {loggedIn ? (en ? "Towards app" : "Spre aplicație") : "Login"} <FontAwesomeIcon icon={faArrowRightLong} />
                            </button>
                            <a className="hp-cta ghost" href="/contact">
                                {en ? "Talk to us" : "Scrie-ne"}
                            </a>
                        </div>
                    </div>

                    <div className="hp-hero-visual ab-visual">
                        <img className="hp-hero-logo" src={ZenLogoWhite} alt="ZenVolt" />
                        <div className="hp-hero-caption">
                            <span className="hp-hero-chip">
                                <FontAwesomeIcon icon={faSitemap} />
                                {en ? "Built in Bucharest" : "Construit în București"}
                            </span>
                            <p>
                                {en
                                    ? "ZenVolt is developed by Zentec, on the electrical schemes and maneuvers used in training station personnel."
                                    : "ZenVolt este dezvoltat de Zentec, pe schemele electrice și manevrele folosite în pregătirea personalului de stație."}
                            </p>
                        </div>
                    </div>
                </section>

                {/* ---- what the platform does ---- */}
                <section className="hp-section">
                    <header className="hp-section-head">
                        <p className="hp-kicker">
                            <span className="hp-kicker-dot" />
                            {en ? "What we build" : "Ce construim"}
                        </p>
                        <h2 className="hp-h2">
                            {en ? <>One platform, <span>three jobs</span></> : <>O platformă, <span>trei roluri</span></>}
                        </h2>
                    </header>

                    <div className="hp-steps">
                        {doing.map((d) => (
                            <article key={d.en} className="hp-step">
                                <span className="hp-step-icon"><FontAwesomeIcon icon={d.icon} /></span>
                                <h3>{en ? d.en : d.ro}</h3>
                                <p>{en ? d.enDesc : d.roDesc}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {/* ---- who it is for ---- */}
                <section className="hp-section">
                    <header className="hp-section-head">
                        <p className="hp-kicker">
                            <span className="hp-kicker-dot" />
                            {en ? "Who it is for" : "Pentru cine"}
                        </p>
                        <h2 className="hp-h2">
                            {en ? <>From the classroom <span>to the shift</span></> : <>De la clasă <span>până în tură</span></>}
                        </h2>
                    </header>

                    <div className="ab-audience">
                        {audience.map((a) => (
                            <article key={a.en} className="ab-audience-card">
                                <span className="hp-benefit-icon"><FontAwesomeIcon icon={a.icon} /></span>
                                <div>
                                    <h3>{en ? a.en : a.ro}</h3>
                                    <p>{en ? a.enDesc : a.roDesc}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* ---- how we work ---- */}
                <section className="hp-section">
                    <header className="hp-section-head">
                        <p className="hp-kicker">
                            <span className="hp-kicker-dot" />
                            {en ? "How we work" : "Cum lucrăm"}
                        </p>
                        <h2 className="hp-h2">
                            {en ? <>Three things we <span>do not bend on</span></> : <>Trei lucruri de la care <span>nu ne abatem</span></>}
                        </h2>
                    </header>

                    <ol className="ab-principles">
                        {principles.map((p, i) => (
                            <li key={p.en}>
                                <span className="ab-principle-no">{`0${i + 1}`}</span>
                                <div>
                                    <h3>{en ? p.en : p.ro}</h3>
                                    <p>{en ? p.enDesc : p.roDesc}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>

                {/* ---- closing ---- */}
                <section className="hp-final">
                    <img src={ZenLogoWhite} alt="ZenVolt" className="hp-final-logo" width={90} height="auto" />

                    <h2 className="hp-final-title">
                        {en ? <><span>Bring the substation</span><br /> into your classroom.</>
                            : <><span>Adu stația</span><br /> în sala ta de curs.</>}
                    </h2>

                    <div className="ab-final-actions">
                        <a className="hp-cta light" href="/contact">
                            {en ? "Talk to us" : "Scrie-ne"} <FontAwesomeIcon icon={faArrowRightLong} />
                        </a>
                        <button type="button" className="hp-cta" onClick={() => navigate(loggedIn ? "/categories" : "/login")}>
                            {loggedIn ? (en ? "Towards app" : "Spre aplicație") : "Login"}
                        </button>
                    </div>

                    <p className="hp-final-meta">
                        <FontAwesomeIcon icon={faCode} /> {en ? "Created by Barbu Mihai." : "Creat de Barbu Mihai."}
                    </p>
                    <p className="hp-final-meta">© {new Date().getFullYear()}. All rights reserved.</p>
                </section>

            </div>
        </div>
    );
};

export default AboutUs;
